// Lib
import * as Immutable from 'immutable';
import { get, isEmpty, compact } from 'lodash/fp';
import axios from 'axios';

// Utils
import http from '../../utils/services/http';
import performQuickSearch from './contentSearch/performQuickSearch';

// Actions
import { getPermissionIdTokens } from '../../utils/permissions/permissionsActions';

// Constants
import {
    ELEMENT_SEARCH_SUBMIT,
    ELEMENT_SEARCH_SUCCESS,
    ELEMENT_SEARCH_FAILURE,
    ELEMENT_SEARCH_CLEAR,
    ELEMENT_SEARCH_SET_QUERY,
} from './searchPopupConstants';
import { TIMES } from '../../../common/utils/timeUtil';

// Types
import {
    ElementSearchRequestQueryParams,
    ElementSearchResponseBody,
} from '../../../common/elements/search/elementSearchRequestTypes';
const EMPTY_LIST = Immutable.List();

const getErrorMessage = get(['error', 'message']);

let cancelSearchRequest: () => void;

export const searchPopupClear =
    () =>
    (dispatch: Function): void => {
        if (cancelSearchRequest) {
            cancelSearchRequest();
        }

        dispatch({ type: ELEMENT_SEARCH_CLEAR });
    };

export const searchPopupSetQuery =
    (query: string) =>
    async (dispatch: Function, getState: Function): Promise<void> => {
        const state = getState();

        const searchResults = await performQuickSearch(state, {
            query,
            results: EMPTY_LIST,
        });

        return dispatch({
            type: ELEMENT_SEARCH_SET_QUERY,
            query,
            searchResults,
        });
    };

export const searchPopupSubmit =
    (_query: string) =>
    async (dispatch: Function, getState: Function): Promise<void> => {
        const state = getState();

        const query = _query || state.getIn(['app', 'popup', 'elementSearch', 'query']);
        const lastQuery = state.getIn(['app', 'popup', 'elementSearch', 'submittedQuery']);

        if (query === lastQuery) return;

        // if there is an existing search request waiting for response, cancel it
        if (cancelSearchRequest) cancelSearchRequest();

        if (!query || !query.length) return;

        const searchResults = await performQuickSearch(state, {
            query,
            results: EMPTY_LIST,
        });

        dispatch({ type: ELEMENT_SEARCH_SUBMIT, query, searchResults });

        const params: ElementSearchRequestQueryParams = {
            query: query.toLowerCase(),
        };

        return http<any, ElementSearchResponseBody>({
            url: 'elements/search',
            params,
            timeout: TIMES.SECOND * 30,
            cancelToken: new axios.CancelToken((cancel) => {
                cancelSearchRequest = cancel;
            }),
        })
            .then(async ({ data }) => {
                const fetchedSearchResults = data.results;

                const permissionIds = compact(fetchedSearchResults.map((result) => result.permissionId));
                const elementIds = fetchedSearchResults.map((result) => result.boardId);

                // If any of the results have associated permission IDs, go fetch the permissions
                if (!isEmpty(permissionIds)) {
                    // Get tokens for the permission IDs
                    await dispatch(getPermissionIdTokens({ permissionIds, elementIds }));
                }

                const updatedSearchResults = await performQuickSearch(state, {
                    query,
                    results: Immutable.fromJS(fetchedSearchResults),
                });

                return dispatch({
                    type: ELEMENT_SEARCH_SUCCESS,
                    results: fetchedSearchResults,
                    resultsTotal: data.total,
                    searchResults: updatedSearchResults,
                });
            })
            .catch((err) => {
                if (axios.isCancel(err)) return;

                let error;

                if (err.response) {
                    error = getErrorMessage(err.response.data);
                } else {
                    error =
                        err.message && err.message.indexOf('timeout') === -1
                            ? err.message.toString()
                            : 'Sorry, something went wrong. Please try again.';
                }

                dispatch({ type: ELEMENT_SEARCH_FAILURE, error });

                return err;
            });
    };
